import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
    FaArrowLeft,
    FaArrowRight,
    FaArrowRight as FaArrowRightIcon,
    FaHeart,
    FaRegHeart,
    FaStar,
} from "react-icons/fa";

import {
    FaFire,
    FaTag,
    FaMapMarkerAlt,
    FaWifi,
    FaSnowflake,
    FaCoffee,
    FaSwimmingPool,
    FaUmbrellaBeach,
    FaSpa,
    FaDumbbell,
    FaParking,
    FaShuttleVan,
    FaUtensils,
    FaCocktail,
    FaConciergeBell,
    FaCheck,
    FaCalendarAlt,
    FaUsers,
    FaBed,
    FaShieldAlt,
    FaCreditCard,
    FaQuoteLeft,
    FaClock,
} from "react-icons/fa";

import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const API = "https://marinadubai.com/api";
const HOTEL_IMG = "https://uaediscountedhotels.com/uploads/hotels";
const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

const SERVICE_CHARGE = 0.10;
const VAT = 0.05;

export default function HotelDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [hotel, setHotel] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [reviews, setReviews] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [bookingLoading, setBookingLoading] = useState(false);

    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");

    const [wishlist, setWishlist] = useState([]);
    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const [showAllReviews, setShowAllReviews] = useState(false);

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (!slug) return;

        const loadHotel = async () => {
            setLoading(true);
            setError(false);

            try {
                const [hotelRes, roomRes, reviewRes] = await Promise.all([
                    axios.get(`${API}/hotels/${slug}`),
                    axios.get(`${API}/hotels/${slug}/rooms`),
                    axios
                        .get(`${API}/hotels/${slug}/reviews`)
                        .catch(() => ({ data: [] })),
                ]);

                setHotel(hotelRes.data);
                setRooms(Array.isArray(roomRes.data) ? roomRes.data : []);
                setReviews(
                    Array.isArray(reviewRes.data) ? reviewRes.data : []
                );
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadHotel();
    }, [slug]);

    useEffect(() => {
        try {
            setWishlist(
                JSON.parse(localStorage.getItem("wishlist")) || []
            );
        } catch {
            setWishlist([]);
        }
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    const isWishlisted = wishlist.some(
        (item) => item.id === hotel?.id
    );

    const toggleWishlist = () => {
        if (!hotel) return;

        const updated = isWishlisted
            ? wishlist.filter((item) => item.id !== hotel.id)
            : [...wishlist, hotel];

        updateWishlist(updated);
    };

    const amenities = useMemo(() => {
        if (!hotel?.amenities) return [];

        if (Array.isArray(hotel.amenities)) {
            return hotel.amenities
                .filter(Boolean)
                .map((item) => String(item).trim());
        }

        return String(hotel.amenities)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }, [hotel]);

    const visibleAmenities = showAllAmenities
        ? amenities
        : amenities.slice(0, 8);

    const gallery = useMemo(() => {
        if (!hotel) return [];

        if (
            Array.isArray(hotel.gallery) &&
            hotel.gallery.length
        ) {
            return hotel.gallery;
        }

        return hotel.image ? [hotel.image] : [];
    }, [hotel]);

    const hotelRating = Number(hotel?.rating || 0);

    const amenityIcon = (name) => {
        const value = String(name).toLowerCase();

        if (value.includes("wifi")) return <FaWifi />;
        if (value.includes("air")) return <FaSnowflake />;
        if (
            value.includes("breakfast") ||
            value.includes("coffee")
        )
            return <FaCoffee />;
        if (value.includes("pool")) return <FaSwimmingPool />;
        if (value.includes("beach")) return <FaUmbrellaBeach />;
        if (value.includes("spa")) return <FaSpa />;
        if (
            value.includes("gym") ||
            value.includes("fitness")
        )
            return <FaDumbbell />;
        if (value.includes("parking")) return <FaParking />;
        if (value.includes("shuttle")) return <FaShuttleVan />;
        if (value.includes("restaurant")) return <FaUtensils />;
        if (value.includes("bar")) return <FaCocktail />;
        if (value.includes("room service"))
            return <FaConciergeBell />;

        return <FaCheck />;
    };

    const getRoomImages = (room) => {
        if (!room) return [];

        if (Array.isArray(room.images)) {
            return room.images.filter(Boolean);
        }

        if (room.images) {
            try {
                const parsed = JSON.parse(room.images);

                if (Array.isArray(parsed)) {
                    return parsed.filter(Boolean);
                }
            } catch { }
        }

        return room.image ? [room.image] : [];
    };

    const getOriginalPrice = (room) =>
        Number(
            room?.price ??
            hotel?.price_per_night ??
            0
        );

    const getSeasonalPrice = (
        date,
        seasonalPrices,
        defaultPrice
    ) => {
        if (
            !Array.isArray(seasonalPrices) ||
            !seasonalPrices.length
        ) {
            return defaultPrice;
        }

        const selectedDate = new Date(date);

        const matched = seasonalPrices.find((season) => {
            const start = new Date(season.start_date);
            const end = new Date(season.end_date);

            return (
                selectedDate >= start &&
                selectedDate <= end
            );
        });

        return matched
            ? Number(matched.price)
            : defaultPrice;
    };

    const getBookingBasePrice = (room) => {
        if (!room) return 0;

        const selectedDate = checkIn
            ? new Date(checkIn)
            : new Date();

        const seasonalPrice = getSeasonalPrice(
            selectedDate,
            room.seasonal_prices || [],
            getOriginalPrice(room)
        );

        return room.discount_price
            ? Number(room.discount_price)
            : seasonalPrice;
    };

    const getBookingSeason = (room) => {
        if (!room) return null;

        const selectedDate = checkIn
            ? new Date(checkIn)
            : new Date();

        return (
            room.seasonal_prices?.find((season) => {
                const start = new Date(season.start_date);
                const end = new Date(season.end_date);

                return (
                    selectedDate >= start &&
                    selectedDate <= end
                );
            }) || null
        );
    };

    const getTourismFee = () =>
        hotelRating === 5 ? 20 : 10;

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diff = end - start;

        const nights = Math.ceil(
            diff / (1000 * 60 * 60 * 24)
        );

        return nights > 0 ? nights : 1;
    };

    const getFinalRoomPrice = (room) => {
        const base = getBookingBasePrice(room);

        return Math.round(
            base +
            base * SERVICE_CHARGE +
            base * VAT +
            getTourismFee()
        );
    };

    const getBookingTotal = () => {
        if (!selectedRoom) return 0;

        return (
            getFinalRoomPrice(selectedRoom) *
            getNights()
        );
    };

    const hasDiscount = (room) => {
        const original = getOriginalPrice(room);
        const discounted = Number(
            room?.discount_price || 0
        );

        return (
            discounted > 0 &&
            discounted < original
        );
    };

    const getRoomPricing = (room) => {
        const selectedDate = checkIn
            ? new Date(checkIn)
            : new Date();

        const seasonalBasePrice = getSeasonalPrice(
            selectedDate,
            room.seasonal_prices || [],
            getOriginalPrice(room)
        );

        const price = room.discount_price
            ? Number(room.discount_price)
            : seasonalBasePrice;

        const discounted = hasDiscount(room);

        const saved = discounted
            ? Math.max(
                seasonalBasePrice - price,
                0
            )
            : 0;

        const percent =
            discounted && seasonalBasePrice
                ? Math.round(
                    (saved / seasonalBasePrice) * 100
                )
                : 0;

        const season =
            room.seasonal_prices?.find((item) => {
                const start = new Date(item.start_date);
                const end = new Date(item.end_date);

                return (
                    selectedDate >= start &&
                    selectedDate <= end
                );
            });

        return {
            price,
            original: seasonalBasePrice,
            saved,
            percent,
            season,
            final: getFinalRoomPrice(room),
        };
    };

    const handleBooking = async () => {
        if (!selectedRoom) {
            alert("Please select a room first.");
            return;
        }

        if (
            !name ||
            !email ||
            !phone ||
            !checkIn ||
            !checkOut
        ) {
            alert("Please fill all booking fields.");
            return;
        }

        if (
            new Date(checkOut) <=
            new Date(checkIn)
        ) {
            alert(
                "Check-out must be after check-in."
            );
            return;
        }

        if (!executeRecaptcha) {
            alert(
                "Captcha is not ready. Please refresh and try again."
            );
            return;
        }

        try {
            setBookingLoading(true);

            const token =
                await executeRecaptcha(
                    "hotel_booking"
                );

            const activeSeason =
                getBookingSeason(selectedRoom);

            const user = JSON.parse(
                localStorage.getItem("user") || "null"
            );

            const res = await axios.post(
                `${API}/hotel-payment`,
                {
                    user_id: user?.id,
                    hotel_id: hotel.id,
                    room_id: selectedRoom.id,
                    room_name:
                        selectedRoom.name ||
                        `Room ${selectedRoom.room_number || ""
                            }`.trim(),
                    room_price:
                        getBookingBasePrice(
                            selectedRoom
                        ),
                    discount_price:
                        selectedRoom.discount_price ||
                        null,
                    season_name:
                        activeSeason?.season_name ||
                        null,
                    season_start:
                        activeSeason?.start_date ||
                        null,
                    season_end:
                        activeSeason?.end_date ||
                        null,
                    name,
                    email,
                    phone,
                    check_in: checkIn,
                    check_out: checkOut,
                    guests:
                        Number(guests) || 1,
                    total_amount:
                        getBookingTotal(),
                    nights: getNights(),
                    captcha: token,
                }
            );

            if (res.data?.payment_url) {
                window.location.href =
                    res.data.payment_url;
            } else {
                alert(
                    "Payment URL was not received."
                );
            }
        } catch (err) {
            console.error(err);

            alert(
                `❌ ${err.response?.data?.error ||
                err.response?.data?.message ||
                "Server error"
                }`
            );
        } finally {
            setBookingLoading(false);
        }
    };

    const sliderSettings = {
        dots: gallery.length > 1,
        infinite: gallery.length > 1,
        speed: 600,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: gallery.length > 1,
        autoplaySpeed: 5000,
        pauseOnHover: false,
        arrows: gallery.length > 1,
        swipe: gallery.length > 1,
        draggable: gallery.length > 1,
        touchMove: gallery.length > 1,
        adaptiveHeight: false,
        cssEase: "ease-in-out",
    };

    const roomSliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        arrows: false,
        autoplay: false,
        dotsClass: "slick-dots !bottom-3",
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F7F5EF]">
                <div className="h-[420px] animate-pulse bg-[#E4E9E6]" />

                <div className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-12 xl:px-16">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">
                        <div className="space-y-5">
                            <div className="h-10 w-2/3 animate-pulse rounded-2xl bg-[#E4E9E6]" />
                            <div className="h-32 animate-pulse rounded-3xl bg-[#E4E9E6]" />
                            <div className="h-72 animate-pulse rounded-3xl bg-[#E4E9E6]" />
                        </div>

                        <div className="h-[600px] animate-pulse rounded-3xl bg-[#E4E9E6]" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !hotel) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F7F5EF] px-5">
                <div className="w-full max-w-md rounded-[28px] border border-[#E4E9E6] bg-white p-10 text-center shadow-[0_20px_60px_rgba(23,35,33,0.10)]">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-2xl text-red-500">
                        !
                    </div>

                    <h1 className="mt-5 text-2xl font-extrabold text-[#172321]">
                        Hotel not found
                    </h1>

                    <p className="mt-2 text-sm leading-6 text-[#6B7774]">
                        We could not load this hotel.
                        Please return to the hotel listing.
                    </p>

                    <button
                        onClick={() =>
                            navigate("/hotels")
                        }
                        className="mt-6 rounded-xl bg-[#0B5552] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#073F3D]"
                    >
                        Browse Hotels
                    </button>
                </div>
            </div>
        );
    }

    const selectedSeason = selectedRoom
        ? getBookingSeason(selectedRoom)
        : null;

    const selectedBase = selectedRoom
        ? getBookingBasePrice(selectedRoom)
        : 0;

    const selectedFinal = selectedRoom
        ? getFinalRoomPrice(selectedRoom)
        : 0;

    return (
        <>
            <Helmet>
                <title>
                    {hotel.meta_title ||
                        hotel.name}
                </title>

                <meta
                    name="description"
                    content={
                        hotel.meta_description ||
                        hotel.description ||
                        ""
                    }
                />

                <link
                    rel="canonical"
                    href={`https://marinadubai.com/hotels/${hotel.slug}`}
                />

                <meta
                    property="og:title"
                    content={
                        hotel.meta_title ||
                        hotel.name
                    }
                />

                <meta
                    property="og:description"
                    content={
                        hotel.meta_description ||
                        hotel.description ||
                        ""
                    }
                />

                <meta
                    property="og:url"
                    content={`https://marinadubai.com/hotels/${hotel.slug}`}
                />

                <meta
                    property="og:image"
                    content={
                        hotel.image
                            ? `${HOTEL_IMG}/${hotel.image}`
                            : "https://marinadubai.com/images/default-og.png"
                    }
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content={
                        hotel.meta_title ||
                        hotel.name
                    }
                />

                <meta
                    name="twitter:description"
                    content={
                        hotel.meta_description ||
                        hotel.description ||
                        ""
                    }
                />

                <meta
                    name="twitter:image"
                    content={
                        hotel.image
                            ? `${HOTEL_IMG}/${hotel.image}`
                            : "https://marinadubai.com/images/default-og.png"
                    }
                />

                <meta
                    name="robots"
                    content="index, follow"
                />
            </Helmet>

            <main className="min-h-screen bg-[#F7F5EF] pb-20 text-[#172321]">
                {/* =========================================================
                    HOTEL HERO
                ========================================================== */}

                <section className="relative w-full overflow-hidden">
                    <div className="relative h-[430px] w-full sm:h-[460px] md:h-[500px] lg:h-[540px] xl:h-[580px]">
                        {gallery.length > 0 ? (
                            <div className="absolute inset-0 z-0">
                                <Slider
                                    {...sliderSettings}
                                    className="hotel-detail-slider h-full"
                                >
                                    {gallery.map(
                                        (
                                            img,
                                            index
                                        ) => (
                                            <div
                                                key={`${img}-${index}`}
                                                className="relative h-[430px] outline-none sm:h-[460px] md:h-[500px] lg:h-[540px] xl:h-[580px]"
                                            >
                                                <img
                                                    src={`${HOTEL_IMG}/${img}`}
                                                    alt={`${hotel.name} ${index +
                                                        1
                                                        }`}
                                                    className="h-full w-full object-cover object-center"
                                                />
                                            </div>
                                        )
                                    )}
                                </Slider>
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-[#073F3D]">
                                <p className="text-sm text-white/70">
                                    No hotel images
                                    available
                                </p>
                            </div>
                        )}

                        {/* Dark teal image overlay */}
                        <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-r from-[#073F3D]/90 via-[#0B5552]/45 to-black/10" />

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-80 bg-gradient-to-t from-[#073F3D]/95 via-[#073F3D]/45 to-transparent" />

                        <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-36 bg-gradient-to-b from-black/35 to-transparent" />

                        {/* Hero content */}
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-end">
                            <div className="mx-auto w-full max-w-[1440px] px-5 pb-12 sm:px-8 sm:pb-14 lg:px-12 lg:pb-16 xl:px-16">
                                <div className="max-w-[900px] text-white">
                                    <div className="mb-4 flex flex-wrap items-center gap-2">
                                        {hotelRating > 0 && (
                                            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#D9A928] px-3 py-1.5 text-xs font-extrabold text-[#172321] shadow-lg">
                                                <FaStar />
                                                {hotelRating.toFixed(
                                                    1
                                                )}
                                            </div>
                                        )}

                                        {hotelRating > 0 && (
                                            <div className="inline-flex items-center rounded-lg border border-white/10 bg-[#0B5552]/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-lg backdrop-blur-md">
                                                {hotelRating}-Star
                                                Hotel
                                            </div>
                                        )}

                                        <div className="inline-flex items-center rounded-lg border border-white/20 bg-black/25 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur-md">
                                            Dubai Marina
                                        </div>
                                    </div>

                                    <h1 className="text-3xl font-extrabold leading-[1.04] tracking-tight text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.35)] sm:text-4xl md:text-5xl lg:text-6xl xl:text-[62px]">
                                        {hotel.name}
                                    </h1>

                                    {hotel.location && (
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                hotel.location
                                            )}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="pointer-events-auto mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/90 transition hover:text-[#D9A928] sm:text-base"
                                        >
                                            <FaMapMarkerAlt className="shrink-0 text-[#D9A928]" />

                                            <span>
                                                {
                                                    hotel.location
                                                }
                                            </span>

                                            <FaArrowRightIcon className="ml-1 text-xs" />
                                        </a>
                                    )}

                                    {hotel.description && (
                                        <p className="mt-3 max-w-[720px] line-clamp-2 text-sm leading-6 text-white/85 drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] sm:text-base sm:leading-7">
                                            {hotel.description
                                                .replace(
                                                    /<[^>]+>/g,
                                                    ""
                                                )
                                                .trim()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Wishlist */}
                        <button
                            type="button"
                            onClick={toggleWishlist}
                            aria-label="Save hotel"
                            className="absolute right-5 top-5 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.25)] backdrop-blur-md transition-all duration-300 hover:scale-110 sm:right-8 sm:top-8 lg:right-10 lg:top-10"
                        >
                            {isWishlisted ? (
                                <FaHeart className="text-lg text-red-500" />
                            ) : (
                                <FaRegHeart className="text-lg text-[#172321]" />
                            )}
                        </button>
                    </div>
                </section>

                {/* =========================================================
                    QUICK HOTEL INFORMATION
                ========================================================== */}

                <section className="relative z-20 -mt-8 px-5 sm:-mt-10 sm:px-8 lg:px-12 xl:px-16">
                    <div className="mx-auto w-full max-w-[1440px] overflow-hidden rounded-[22px] border border-white/80 bg-white shadow-[0_20px_55px_rgba(23,35,33,0.12)]">
                        <div className="grid grid-cols-2 lg:grid-cols-4">
                            {/* Location */}
                            <div className="flex items-center gap-3 border-b border-slate-100 p-4 sm:p-5 lg:border-b-0 lg:border-r">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F2F0] text-[#0B5552]">
                                    <FaMapMarkerAlt />
                                </div>

                                <div className="min-w-0">
                                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#87918E]">
                                        Location
                                    </p>

                                    <p className="mt-1 truncate text-xs font-bold text-[#172321] sm:text-sm">
                                        {hotel.location ||
                                            "Dubai, UAE"}
                                    </p>
                                </div>
                            </div>

                            {/* Rooms */}
                            <div className="flex items-center gap-3 border-b border-slate-100 p-4 sm:p-5 lg:border-b-0 lg:border-r">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F7F1DC] text-[#A37A13]">
                                    <FaBed />
                                </div>

                                <div>
                                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#87918E]">
                                        Rooms
                                    </p>

                                    <p className="mt-1 text-xs font-bold text-[#172321] sm:text-sm">
                                        {rooms.length}{" "}
                                        available
                                    </p>
                                </div>
                            </div>

                            {/* Guests */}
                            <div className="flex items-center gap-3 border-r border-slate-100 p-4 sm:p-5">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F2F0] text-[#0B5552]">
                                    <FaUsers />
                                </div>

                                <div>
                                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#87918E]">
                                        Guests
                                    </p>

                                    <p className="mt-1 text-xs font-bold text-[#172321] sm:text-sm">
                                        {guests} guest
                                        {guests !==
                                            1
                                            ? "s"
                                            : ""}
                                    </p>
                                </div>
                            </div>

                            {/* Secure */}
                            <div className="flex items-center gap-3 p-4 sm:p-5">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F7F1DC] text-[#A37A13]">
                                    <FaShieldAlt />
                                </div>

                                <div>
                                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#87918E]">
                                        Booking
                                    </p>

                                    <p className="mt-1 text-xs font-bold text-[#172321] sm:text-sm">
                                        Secure &
                                        trusted
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    MAIN CONTENT
                ========================================================== */}

                <section className="mx-auto mt-8 w-full max-w-[1440px] px-5 sm:px-8 lg:mt-10 lg:px-12 xl:px-16">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">
                        {/* =================================================
                            LEFT CONTENT
                        ================================================== */}

                        <div className="space-y-8">
                            {/* ABOUT */}
                            <section className="overflow-hidden rounded-[28px] border border-[#E4E9E6] bg-white shadow-[0_15px_45px_rgba(23,35,33,0.06)]">
                                <div className="p-6 sm:p-8 lg:p-9">
                                    <div className="mb-7 flex items-start gap-4">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0B5552] text-white shadow-lg shadow-[#0B5552]/20">
                                            <FaHotelIcon />
                                        </div>

                                        <div>
                                            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0B5552]">
                                                Your stay
                                            </p>

                                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-[#172321] sm:text-3xl">
                                                About this
                                                hotel
                                            </h2>
                                        </div>
                                    </div>

                                    <div
                                        className="hotel-content prose prose-slate max-w-none leading-7 text-[#52605D]"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                hotel.description ||
                                                "A comfortable stay in Dubai.",
                                        }}
                                    />
                                </div>

                                {/* Amenities */}
                                {amenities.length > 0 && (
                                    <div className="border-t border-[#E4E9E6] bg-[#FBFAF6] p-6 sm:p-8 lg:p-9">
                                        <div className="mb-5 flex items-end justify-between gap-4">
                                            <div>
                                                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0B5552]">
                                                    Hotel
                                                    facilities
                                                </p>

                                                <h3 className="mt-1 text-xl font-extrabold text-[#172321]">
                                                    Everything
                                                    you need
                                                </h3>
                                            </div>

                                            {amenities.length >
                                                8 && (
                                                    <button
                                                        onClick={() =>
                                                            setShowAllAmenities(
                                                                !showAllAmenities
                                                            )
                                                        }
                                                        className="text-sm font-bold text-[#0B5552] transition hover:text-[#073F3D]"
                                                    >
                                                        {showAllAmenities
                                                            ? "Show less"
                                                            : `View all ${amenities.length}`}
                                                    </button>
                                                )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            {visibleAmenities.map(
                                                (
                                                    amenity,
                                                    index
                                                ) => (
                                                    <div
                                                        key={`${amenity}-${index}`}
                                                        className="group flex items-center gap-3 rounded-2xl border border-[#E4E9E6] bg-white p-3.5 transition hover:border-[#0B5552]/30 hover:shadow-md"
                                                    >
                                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8F2F0] text-[#0B5552] transition group-hover:bg-[#0B5552] group-hover:text-white">
                                                            {amenityIcon(
                                                                amenity
                                                            )}
                                                        </div>

                                                        <span className="text-xs font-semibold text-[#52605D] sm:text-sm">
                                                            {
                                                                amenity
                                                            }
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Check-in/out */}
                                {(hotel.check_in_time ||
                                    hotel.check_out_time) && (
                                        <div className="grid gap-3 border-t border-[#E4E9E6] p-6 sm:grid-cols-2 sm:p-8">
                                            {hotel.check_in_time && (
                                                <div className="flex items-center gap-3 rounded-2xl bg-[#F7F5EF] p-4">
                                                    <FaClock className="text-[#0B5552]" />

                                                    <div>
                                                        <p className="text-xs text-[#87918E]">
                                                            Check-in
                                                        </p>

                                                        <p className="font-bold text-[#172321]">
                                                            {
                                                                hotel.check_in_time
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {hotel.check_out_time && (
                                                <div className="flex items-center gap-3 rounded-2xl bg-[#F7F5EF] p-4">
                                                    <FaClock className="text-[#0B5552]" />

                                                    <div>
                                                        <p className="text-xs text-[#87918E]">
                                                            Check-out
                                                        </p>

                                                        <p className="font-bold text-[#172321]">
                                                            {
                                                                hotel.check_out_time
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </section>

                            {/* =================================================
                                ROOMS
                            ================================================== */}

                            <section
                                id="rooms"
                                className="scroll-mt-24"
                            >
                                <div className="mb-5 flex items-end justify-between gap-4">
                                    <div>
                                        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0B5552]">
                                            Choose your
                                            room
                                        </p>

                                        <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-[#172321] sm:text-3xl">
                                            Available rooms
                                        </h2>
                                    </div>

                                    <span className="hidden text-sm text-[#6B7774] sm:block">
                                        {rooms.length}{" "}
                                        option
                                        {rooms.length !==
                                            1
                                            ? "s"
                                            : ""}
                                    </span>
                                </div>

                                <div className="space-y-5">
                                    {rooms.map((room) => {
                                        const images =
                                            getRoomImages(
                                                room
                                            );

                                        const pricing =
                                            getRoomPricing(
                                                room
                                            );

                                        const isSelected =
                                            selectedRoom?.id ===
                                            room.id;

                                        return (
                                            <article
                                                key={
                                                    room.id
                                                }
                                                onClick={() =>
                                                    setSelectedRoom(
                                                        room
                                                    )
                                                }
                                                className={`cursor-pointer overflow-hidden rounded-[28px] border bg-white shadow-[0_12px_35px_rgba(23,35,33,0.06)] transition-all duration-300 ${isSelected
                                                    ? "border-[#0B5552] ring-4 ring-[#0B5552]/10 shadow-[0_18px_50px_rgba(11,85,82,0.13)]"
                                                    : "border-[#E4E9E6] hover:border-[#0B5552]/30 hover:shadow-[0_18px_45px_rgba(23,35,33,0.10)]"
                                                    }`}
                                            >
                                                <div className="grid lg:grid-cols-[340px_minmax(0,1fr)]">
                                                    {/* Image */}
                                                    <div className="relative min-h-[270px] overflow-hidden bg-[#E4E9E6] lg:h-full">
                                                        {images.length ? (
                                                            <Slider
                                                                {...roomSliderSettings}
                                                            >
                                                                {images.map(
                                                                    (
                                                                        img,
                                                                        index
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                index
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={`${ROOM_IMG}/${img}`}
                                                                                alt={
                                                                                    room.name ||
                                                                                    `Room ${index +
                                                                                    1
                                                                                    }`
                                                                                }
                                                                                className="h-[270px] w-full object-cover lg:h-[310px]"
                                                                            />
                                                                        </div>
                                                                    )
                                                                )}
                                                            </Slider>
                                                        ) : (
                                                            <div className="flex h-full min-h-[270px] items-center justify-center text-sm text-[#87918E]">
                                                                No
                                                                room
                                                                image
                                                            </div>
                                                        )}

                                                        <div className="absolute left-4 top-4 z-10 flex flex-wrap gap-2">
                                                            {pricing.season && (
                                                                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D9A928]/40 bg-[#F7F1DC] px-3 py-1.5 text-[10px] font-bold text-[#80651A] shadow">
                                                                    <FaFire className="text-[#C08D14]" />
                                                                    Seasonal
                                                                    offer
                                                                </span>
                                                            )}

                                                            {pricing.percent >
                                                                0 && (
                                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0B5552] px-3 py-1.5 text-[10px] font-bold text-white shadow">
                                                                        <FaTag />
                                                                        {
                                                                            pricing.percent
                                                                        }
                                                                        %
                                                                        OFF
                                                                    </span>
                                                                )}
                                                        </div>

                                                        {isSelected && (
                                                            <div className="absolute bottom-4 left-4 z-10 rounded-full bg-[#0B5552] px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                                                                Selected
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Room information */}
                                                    <div className="flex flex-col justify-between p-5 sm:p-6 lg:p-7">
                                                        <div>
                                                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                                                <div>
                                                                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B5552]">
                                                                        {room.room_number
                                                                            ? `Room ${room.room_number}`
                                                                            : "Premium room"}
                                                                    </p>

                                                                    <h3 className="mt-1 text-xl font-extrabold text-[#172321]">
                                                                        {room.name ||
                                                                            `Room ${room.room_number ||
                                                                            ""
                                                                            }`}
                                                                    </h3>

                                                                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#6B7774]">
                                                                        <span className="inline-flex items-center gap-1.5">
                                                                            <FaUsers className="text-[#0B5552]" />
                                                                            {room.capacity ||
                                                                                1}{" "}
                                                                            guests
                                                                        </span>

                                                                        <span className="inline-flex items-center gap-1.5">
                                                                            <FaBed className="text-[#0B5552]" />
                                                                            {room.beds_count
                                                                                ? `${room.beds_count} beds`
                                                                                : "1 bed"}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="shrink-0 sm:text-right">
                                                                    <div className="flex items-end gap-1 sm:justify-end">
                                                                        <span className="text-2xl font-extrabold text-[#0B5552]">
                                                                            {pricing.price.toLocaleString()}
                                                                        </span>

                                                                        <span className="mb-1 text-xs text-[#87918E]">
                                                                            AED
                                                                            /
                                                                            night
                                                                        </span>
                                                                    </div>

                                                                    {pricing.percent >
                                                                        0 && (
                                                                            <>
                                                                                <p className="text-xs text-[#87918E] line-through">
                                                                                    {pricing.original.toLocaleString()}{" "}
                                                                                    AED
                                                                                </p>

                                                                                <p className="mt-1 text-xs font-bold text-[#0B5552]">
                                                                                    Save{" "}
                                                                                    {pricing.saved.toLocaleString()}{" "}
                                                                                    AED
                                                                                </p>
                                                                            </>
                                                                        )}
                                                                </div>
                                                            </div>

                                                            {/* Room facilities */}
                                                            <div className="mt-5 flex flex-wrap gap-2">
                                                                {[
                                                                    [
                                                                        "WiFi",
                                                                        <FaWifi />,
                                                                    ],
                                                                    [
                                                                        "Air conditioning",
                                                                        <FaSnowflake />,
                                                                    ],
                                                                    [
                                                                        "Breakfast",
                                                                        <FaCoffee />,
                                                                    ],
                                                                ].map(
                                                                    ([
                                                                        label,
                                                                        icon,
                                                                    ]) => (
                                                                        <span
                                                                            key={
                                                                                label
                                                                            }
                                                                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9E6] bg-[#FBFAF6] px-3 py-1.5 text-xs font-medium text-[#52605D]"
                                                                        >
                                                                            <span className="text-[#0B5552]">
                                                                                {
                                                                                    icon
                                                                                }
                                                                            </span>
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                    )
                                                                )}
                                                            </div>

                                                            {pricing.season && (
                                                                <div className="mt-4 rounded-2xl border border-[#D9A928]/30 bg-[#F7F1DC] p-3">
                                                                    <div className="flex gap-2">
                                                                        <FaCalendarAlt className="mt-0.5 text-[#A37A13]" />

                                                                        <div>
                                                                            <p className="text-xs font-bold text-[#725B15]">
                                                                                {
                                                                                    pricing
                                                                                        .season
                                                                                        .season_name
                                                                                }{" "}
                                                                                pricing
                                                                            </p>

                                                                            <p className="mt-0.5 text-[11px] text-[#8D7425]">
                                                                                {
                                                                                    pricing
                                                                                        .season
                                                                                        .start_date
                                                                                }{" "}
                                                                                →
                                                                                {
                                                                                    pricing
                                                                                        .season
                                                                                        .end_date
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Buttons */}
                                                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                                            <button
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();

                                                                    navigate(
                                                                        `/hotels/${hotel.slug}/${room.id}`
                                                                    );
                                                                }}
                                                                className="rounded-xl border border-[#DDE3E0] py-3 text-sm font-bold text-[#52605D] transition hover:border-[#0B5552] hover:text-[#0B5552]"
                                                            >
                                                                View room
                                                                details
                                                            </button>

                                                            <button
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();

                                                                    setSelectedRoom(
                                                                        room
                                                                    );

                                                                    document
                                                                        .getElementById(
                                                                            "booking-panel"
                                                                        )
                                                                        ?.scrollIntoView(
                                                                            {
                                                                                behavior:
                                                                                    "smooth",
                                                                                block: "start",
                                                                            }
                                                                        );
                                                                }}
                                                                className={`rounded-xl py-3 text-sm font-extrabold transition ${isSelected
                                                                    ? "bg-[#0B5552] text-white shadow-lg shadow-[#0B5552]/20"
                                                                    : "bg-[#D9A928] text-[#172321] shadow-md hover:bg-[#E7B735]"
                                                                    }`}
                                                            >
                                                                {isSelected
                                                                    ? "Room selected ✓"
                                                                    : "Select this room"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}

                                    {!rooms.length && (
                                        <div className="rounded-[28px] border border-[#E4E9E6] bg-white p-12 text-center">
                                            <FaBed className="mx-auto text-3xl text-[#C5CDCA]" />

                                            <h3 className="mt-4 text-lg font-bold text-[#172321]">
                                                No rooms
                                                available
                                            </h3>

                                            <p className="mt-1 text-sm text-[#6B7774]">
                                                Please check
                                                again later.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* =================================================
                                REVIEWS
                            ================================================== */}

                            <section className="overflow-hidden rounded-[28px] border border-[#E4E9E6] bg-white shadow-[0_15px_45px_rgba(23,35,33,0.06)]">
                                <div className="border-b border-[#E4E9E6] p-6 sm:p-8">
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0B5552]">
                                                Guest
                                                experience
                                            </p>

                                            <h2 className="mt-1 text-2xl font-extrabold text-[#172321] sm:text-3xl">
                                                Guest reviews
                                            </h2>

                                            <p className="mt-2 text-sm text-[#6B7774]">
                                                Honest feedback
                                                from guests
                                                who stayed
                                                here.
                                            </p>
                                        </div>

                                        {reviews.length >
                                            0 && (
                                                <div className="rounded-2xl border border-[#D9A928]/30 bg-[#F7F1DC] px-5 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-2xl font-extrabold text-[#172321]">
                                                            {(
                                                                reviews.reduce(
                                                                    (
                                                                        sum,
                                                                        review
                                                                    ) =>
                                                                        sum +
                                                                        Number(
                                                                            review.rating ||
                                                                            0
                                                                        ),
                                                                    0
                                                                ) /
                                                                reviews.length
                                                            ).toFixed(
                                                                1
                                                            )}
                                                        </span>

                                                        <div>
                                                            <div className="tracking-tight text-[#C09018]">
                                                                ★★★★★
                                                            </div>

                                                            <p className="text-[10px] text-[#6B7774]">
                                                                {
                                                                    reviews.length
                                                                }{" "}
                                                                review
                                                                {reviews.length !==
                                                                    1
                                                                    ? "s"
                                                                    : ""}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                </div>

                                <div className="p-6 sm:p-8">
                                    {reviews.length ===
                                        0 ? (
                                        <div className="py-14 text-center">
                                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F7F1DC] text-[#C09018]">
                                                <FaStar className="text-2xl" />
                                            </div>

                                            <h3 className="mt-4 text-lg font-bold">
                                                No reviews
                                                yet
                                            </h3>

                                            <p className="mt-1 text-sm text-[#6B7774]">
                                                Be the first
                                                guest to
                                                share your
                                                experience.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-4">
                                                {(showAllReviews
                                                    ? reviews
                                                    : reviews.slice(
                                                        0,
                                                        4
                                                    )
                                                ).map(
                                                    (
                                                        review
                                                    ) => {
                                                        const rawName =
                                                            review
                                                                .email
                                                                ?.split(
                                                                    "@"
                                                                )[0] ||
                                                            "Guest";

                                                        const displayName =
                                                            rawName
                                                                .replace(
                                                                    /\./g,
                                                                    " "
                                                                )
                                                                .replace(
                                                                    /\b\w/g,
                                                                    (
                                                                        letter
                                                                    ) =>
                                                                        letter.toUpperCase()
                                                                );

                                                        const rating =
                                                            Number(
                                                                review.rating ||
                                                                0
                                                            );

                                                        return (
                                                            <article
                                                                key={
                                                                    review.id
                                                                }
                                                                className="rounded-2xl border border-[#E4E9E6] p-5 transition hover:border-[#0B5552]/20 hover:shadow-md"
                                                            >
                                                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B5552] font-bold text-white">
                                                                            {displayName.charAt(
                                                                                0
                                                                            )}
                                                                        </div>

                                                                        <div>
                                                                            <h4 className="text-sm font-bold text-[#172321]">
                                                                                {
                                                                                    displayName
                                                                                }
                                                                            </h4>

                                                                            <div className="mt-1 flex items-center gap-2">
                                                                                <span className="rounded-full bg-[#E8F2F0] px-2 py-1 text-[10px] font-bold text-[#0B5552]">
                                                                                    Verified
                                                                                    Guest
                                                                                </span>

                                                                                <span className="text-[11px] text-[#87918E]">
                                                                                    {dayjs(
                                                                                        review.created_at
                                                                                    ).fromNow()}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="inline-flex items-center gap-2 self-start rounded-xl border border-[#D9A928]/30 bg-[#F7F1DC] px-3 py-2">
                                                                        <span className="font-extrabold">
                                                                            {rating.toFixed(
                                                                                1
                                                                            )}
                                                                        </span>

                                                                        <span className="text-sm text-[#C09018]">
                                                                            {"★".repeat(
                                                                                Math.min(
                                                                                    rating,
                                                                                    5
                                                                                )
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-5 flex gap-3 border-t border-[#E4E9E6] pt-4">
                                                                    <FaQuoteLeft className="mt-1 shrink-0 text-[#D9A928]" />

                                                                    <p className="text-sm leading-7 text-[#52605D]">
                                                                        {
                                                                            review.comment
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </article>
                                                        );
                                                    }
                                                )}
                                            </div>

                                            {reviews.length >
                                                4 && (
                                                    <button
                                                        onClick={() =>
                                                            setShowAllReviews(
                                                                !showAllReviews
                                                            )
                                                        }
                                                        className="mt-6 w-full rounded-xl border border-[#0B5552]/20 py-3 text-sm font-bold text-[#0B5552] transition hover:bg-[#E8F2F0]"
                                                    >
                                                        {showAllReviews
                                                            ? "Show fewer reviews"
                                                            : `View all ${reviews.length} reviews`}
                                                    </button>
                                                )}
                                        </>
                                    )}
                                </div>
                            </section>
                        </div>

                        {/* =================================================
                            BOOKING PANEL
                        ================================================== */}

                        <aside
                            id="booking-panel"
                            className="scroll-mt-24 lg:sticky lg:top-24"
                        >
                            <div className="overflow-hidden rounded-[28px] border border-[#E4E9E6] bg-white shadow-[0_20px_60px_rgba(23,35,33,0.12)]">
                                {/* Booking header */}
                                <div className="relative overflow-hidden bg-[#073F3D] p-6 text-white sm:p-7">
                                    <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#0B5552]/60 blur-2xl" />

                                    <div className="absolute -bottom-12 -right-4 h-28 w-28 rounded-full bg-[#D9A928]/20 blur-2xl" />

                                    <div className="relative">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D9A928]">
                                            Reserve your
                                            stay
                                        </p>

                                        <h2 className="mt-1 text-2xl font-extrabold !text-white">
                                            Book this hotel
                                        </h2>

                                        <p className="mt-2 text-xs text-white/65">
                                            Secure checkout
                                            with instant
                                            confirmation.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4 p-5 sm:p-6">
                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wider text-[#87918E]">
                                                Check-in
                                            </label>

                                            <div className="relative mt-1">
                                                <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#0B5552]" />

                                                <input
                                                    type="date"
                                                    value={
                                                        checkIn
                                                    }
                                                    min={
                                                        today
                                                    }
                                                    onChange={(
                                                        e
                                                    ) => {
                                                        setCheckIn(
                                                            e
                                                                .target
                                                                .value
                                                        );

                                                        if (
                                                            checkOut &&
                                                            e
                                                                .target
                                                                .value >=
                                                            checkOut
                                                        ) {
                                                            setCheckOut(
                                                                ""
                                                            );
                                                        }
                                                    }}
                                                    className="w-full rounded-xl border border-[#DDE3E0] bg-white py-3 pl-8 pr-2 text-xs outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wider text-[#87918E]">
                                                Check-out
                                            </label>

                                            <div className="relative mt-1">
                                                <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#0B5552]" />

                                                <input
                                                    type="date"
                                                    value={
                                                        checkOut
                                                    }
                                                    min={
                                                        checkIn ||
                                                        today
                                                    }
                                                    onChange={(
                                                        e
                                                    ) =>
                                                        setCheckOut(
                                                            e
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                    className="w-full rounded-xl border border-[#DDE3E0] bg-white py-3 pl-8 pr-2 text-xs outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Guests */}
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-[#87918E]">
                                            Guests
                                        </label>

                                        <div className="relative mt-1">
                                            <FaUsers className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#0B5552]" />

                                            <input
                                                type="number"
                                                min="1"
                                                value={
                                                    guests
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setGuests(
                                                        Math.max(
                                                            1,
                                                            Number(
                                                                e
                                                                    .target
                                                                    .value
                                                            ) ||
                                                            1
                                                        )
                                                    )
                                                }
                                                className="w-full rounded-xl border border-[#DDE3E0] py-3 pl-8 pr-3 text-sm outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                            />
                                        </div>
                                    </div>

                                    <div className="h-px bg-[#E4E9E6]" />

                                    {/* Guest details */}
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B5552]">
                                            Guest details
                                        </p>

                                        <div className="mt-3 space-y-3">
                                            <input
                                                type="text"
                                                value={
                                                    name
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setName(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="Full name"
                                                className="w-full rounded-xl border border-[#DDE3E0] px-4 py-3 text-sm outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                            />

                                            <input
                                                type="email"
                                                value={
                                                    email
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setEmail(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="Email address"
                                                className="w-full rounded-xl border border-[#DDE3E0] px-4 py-3 text-sm outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                            />

                                            <input
                                                type="tel"
                                                value={
                                                    phone
                                                }
                                                onChange={(
                                                    e
                                                ) =>
                                                    setPhone(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="+971 4 447 3839"
                                                className="w-full rounded-xl border border-[#DDE3E0] px-4 py-3 text-sm outline-none transition focus:border-[#0B5552] focus:ring-2 focus:ring-[#0B5552]/10"
                                            />
                                        </div>
                                    </div>

                                    {/* Selected room */}
                                    {!selectedRoom ? (
                                        <div className="rounded-2xl border border-[#DCEAE7] bg-[#E8F2F0] p-4">
                                            <div className="flex gap-3">
                                                <FaBed className="mt-0.5 text-[#0B5552]" />

                                                <div>
                                                    <p className="text-sm font-bold text-[#172321]">
                                                        Select a
                                                        room to
                                                        continue
                                                    </p>

                                                    <p className="mt-1 text-xs leading-5 text-[#6B7774]">
                                                        Choose
                                                        one of
                                                        the
                                                        rooms
                                                        below
                                                        and the
                                                        full
                                                        price
                                                        will
                                                        appear
                                                        here.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="overflow-hidden rounded-2xl border border-[#DCEAE7] bg-[#FBFDFC]">
                                            <div className="border-b border-[#DCEAE7] bg-[#E8F2F0] p-4">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#0B5552]">
                                                    Selected
                                                    room
                                                </p>

                                                <p className="mt-1 font-bold text-[#172321]">
                                                    {selectedRoom.name ||
                                                        `Room ${selectedRoom.room_number ||
                                                        ""
                                                        }`}
                                                </p>
                                            </div>

                                            <div className="space-y-3 p-4">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-[#6B7774]">
                                                        Base /
                                                        night
                                                    </span>

                                                    <span className="font-semibold">
                                                        {selectedBase.toLocaleString()}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                {selectedSeason && (
                                                    <div className="rounded-xl border border-[#D9A928]/30 bg-[#F7F1DC] p-3">
                                                        <p className="text-xs font-bold text-[#725B15]">
                                                            {
                                                                selectedSeason.season_name
                                                            }{" "}
                                                            pricing
                                                            applied
                                                        </p>

                                                        <p className="mt-1 text-[10px] text-[#8D7425]">
                                                            {
                                                                selectedSeason.start_date
                                                            }{" "}
                                                            →
                                                            {
                                                                selectedSeason.end_date
                                                            }
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="flex justify-between text-xs text-[#6B7774]">
                                                    <span>
                                                        Service
                                                        charge
                                                        (10%)
                                                    </span>

                                                    <span>
                                                        {(
                                                            selectedBase *
                                                            SERVICE_CHARGE
                                                        ).toLocaleString(
                                                            undefined,
                                                            {
                                                                maximumFractionDigits: 0,
                                                            }
                                                        )}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                <div className="flex justify-between text-xs text-[#6B7774]">
                                                    <span>
                                                        VAT
                                                        (5%)
                                                    </span>

                                                    <span>
                                                        {(
                                                            selectedBase *
                                                            VAT
                                                        ).toLocaleString(
                                                            undefined,
                                                            {
                                                                maximumFractionDigits: 0,
                                                            }
                                                        )}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                <div className="flex justify-between text-xs text-[#6B7774]">
                                                    <span>
                                                        Tourism
                                                        fee
                                                    </span>

                                                    <span>
                                                        {getTourismFee()}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                <div className="flex justify-between border-t border-[#DCEAE7] pt-3">
                                                    <span className="text-sm font-bold">
                                                        Final /
                                                        night
                                                    </span>

                                                    <span className="font-extrabold text-[#0B5552]">
                                                        {selectedFinal.toLocaleString()}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                {checkIn &&
                                                    checkOut && (
                                                        <div className="rounded-xl border border-[#E4E9E6] bg-white p-3">
                                                            <div className="flex justify-between text-sm">
                                                                <span className="text-[#6B7774]">
                                                                    {getNights()}{" "}
                                                                    night
                                                                    {getNights() !==
                                                                        1
                                                                        ? "s"
                                                                        : ""}
                                                                </span>

                                                                <span className="font-extrabold text-[#172321]">
                                                                    {getBookingTotal().toLocaleString()}{" "}
                                                                    AED
                                                                </span>
                                                            </div>

                                                            <p className="mt-1 text-[10px] text-[#87918E]">
                                                                Taxes
                                                                and
                                                                fees
                                                                included
                                                            </p>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Book */}
                                    <button
                                        onClick={
                                            handleBooking
                                        }
                                        disabled={
                                            !selectedRoom ||
                                            bookingLoading
                                        }
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#D9A928] py-4 text-sm font-extrabold text-[#172321] shadow-lg shadow-[#D9A928]/20 transition hover:-translate-y-0.5 hover:bg-[#E7B735] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                                    >
                                        {bookingLoading ? (
                                            <>
                                                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#172321]/30 border-t-[#172321]" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Confirm &
                                                Book Now
                                                <FaArrowRight />
                                            </>
                                        )}
                                    </button>

                                    <div className="flex items-center justify-center gap-2 text-[10px] text-[#87918E]">
                                        <FaLockIcon />
                                        Secure payment •
                                        Instant
                                        confirmation
                                    </div>
                                </div>
                            </div>

                            {/* Trust cards */}
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="rounded-2xl border border-[#E4E9E6] bg-white p-4 text-center shadow-sm">
                                    <FaShieldAlt className="mx-auto text-[#0B5552]" />

                                    <p className="mt-2 text-xs font-bold text-[#172321]">
                                        Trusted booking
                                    </p>
                                </div>

                                <div className="rounded-2xl border border-[#E4E9E6] bg-white p-4 text-center shadow-sm">
                                    <FaCreditCard className="mx-auto text-[#0B5552]" />

                                    <p className="mt-2 text-xs font-bold text-[#172321]">
                                        Secure payment
                                    </p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>

            {/* =============================================================
                MOBILE RESERVE BAR
            ============================================================= */}

            <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#DDE3E0] bg-white/95 p-3 shadow-[0_-8px_30px_rgba(23,35,33,0.12)] backdrop-blur-xl lg:hidden">
                <div className="mx-auto flex max-w-2xl items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#87918E]">
                            {selectedRoom
                                ? "Selected room"
                                : "Starting from"}
                        </p>

                        <p className="truncate font-extrabold text-[#0B5552]">
                            {selectedRoom
                                ? `${getFinalRoomPrice(
                                    selectedRoom
                                ).toLocaleString()} AED / night`
                                : rooms.length
                                    ? `${Math.min(
                                        ...rooms.map(
                                            (room) =>
                                                getFinalRoomPrice(
                                                    room
                                                )
                                        )
                                    ).toLocaleString()} AED / night`
                                    : "Check rooms"}
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            if (!selectedRoom) {
                                document
                                    .getElementById(
                                        "rooms"
                                    )
                                    ?.scrollIntoView({
                                        behavior:
                                            "smooth",
                                    });
                            } else {
                                document
                                    .getElementById(
                                        "booking-panel"
                                    )
                                    ?.scrollIntoView({
                                        behavior:
                                            "smooth",
                                    });
                            }
                        }}
                        className="rounded-xl bg-[#D9A928] px-5 py-3 text-sm font-extrabold text-[#172321] shadow-lg transition hover:bg-[#E7B735]"
                    >
                        {selectedRoom
                            ? "Reserve"
                            : "Choose Room"}
                    </button>
                </div>
            </div>
        </>
    );
}

/* Small local icon wrappers */
function FaHotelIcon(props) {
    return <FaConciergeBell {...props} />;
}

function FaLockIcon(props) {
    return <FaShieldAlt {...props} />;
}